// @ts-check

import FileRepoFile from '../files/FileRepoFile';
import { fileErrors } from '../files/validation';
import { tryParseResponse, get, post } from './fetch';

/**
 * @module network/fileRepo
 */

/**
 * Uploads a file to FileRepo and returns the file's new file repo id
 * @param {File} file - The file to upload, from for example an <input type="file" />
 * @param {object} [fileRepoData] - File repo connection and authorization parameters
 * @param {string} [fileRepoData.endpoint]
 * @param {string} [fileRepoData.authorizationHeaderKey]
 * @param {string} [fileRepoData.token]
 * @param {string} [fileRepoData.validationHeaderKey]
 * @param {string} [fileRepoData.validationHeader]
 * @param {string} [fileRepoData.publicUploadHeaderKey]
 * @param {string} [fileRepoData.publicUploadHeader]
 * @param {string} [fileName=file.name] name of the file, defaults to file.name
 * @param {boolean} [isTemporary=false] - If true, the file will be deleted after a while automatically
 * @returns {Promise} A promise that resolves to { fileGuid, fileName, mimeType, signedFileRepoId, signedGuid, error }
 *  Error will be one of: (null, 'invalidFileName', 'invalidExtension', 'tooLarge', 'tooSmall', 'unknown')
 */
export function uploadFileToFileRepo(file, fileRepoData, fileName, isTemporary = false) {
    fileName = fileName || file.name;

    const formData = new FormData();

    formData.append('file', file, fileName);

    const headers = {
        [fileRepoData.authorizationHeaderKey]: 'Bearer ' + fileRepoData.token,
        [fileRepoData.validationHeaderKey]: fileRepoData.validationHeader,
        [fileRepoData.publicUploadHeaderKey]: fileRepoData.publicUploadHeader,
        Accept: 'application/json, text/javascript, */*; q=0.01'
    };

    if (isTemporary) {
        headers[fileRepoData.tempFileHeaderKey] = '1';
    }

    return fetch(fileRepoData.endpoint, {
        body: formData,
        method: 'POST',
        mode: 'cors',
        headers,
    }).then(tryParseResponse)
        .then(response => {
            if (response.error) {
                return Object.assign({ name: fileName }, FileRepoFile.fromFileRepo(response));
            } else {
                return FileRepoFile.fromFileRepo(response);
            }
        })
        .catch((response) => {
            return { name: fileName, error: fileErrors.unknown };
        });
}

/**
 * Uploads a file from an external source to FileRepo and returns the file's new file repo id
 * @param {number} fileId - The original file ID
 * @param {string} fileUrl - The url of the file to upload
 * @param {string} fileName - The name of the file to upload
 * @param {number} fileSize - The size of the file to upload
 * @param {string} fileSourceType - The source type of the file to upload
 * @param {object} [fileRepoData] - File repo connection and authorization parameters
 * @param {string} [fileRepoData.endpoint]
 * @param {string} [fileRepoData.authorizationHeaderKey]
 * @param {string} [fileRepoData.token]
 * @param {string} [fileRepoData.validationHeaderKey]
 * @param {string} [fileRepoData.validationHeader]
 * @param {string} [fileRepoData.publicUploadFromExternalSourceHeaderKey]
 * @param {string} [fileRepoData.publicUploadFromExternalSourceHeader]
 * @returns {Promise} A promise that resolves to { fileGuid, fileName, mimeType, signedFileRepoId, signedGuid, originalFileId, sourceType, error }
 *  Error will be one of: (null, 'invalidFileName', 'invalidExtension', 'tooLarge', 'tooSmall', 'unknown')
 */
export function uploadFileFromExternalSourceToFileRepo(fileId, fileUrl, fileName, fileSize, fileSourceType, fileRepoData) {
    const data = {
        Url: fileUrl,
        Name: fileName,
        Size: fileSize,
        SourceType: fileSourceType
    };

    return post(fileRepoData.endpoint, data, {
        headers: {
            [fileRepoData.authorizationHeaderKey]: 'Bearer ' + fileRepoData.token,
            [fileRepoData.validationHeaderKey]: fileRepoData.validationHeader,
            [fileRepoData.publicUploadFromExternalSourceHeaderKey]: fileRepoData.publicUploadFromExternalSourceHeader,
            Accept: 'application/json, text/javascript, */*; q=0.01',
        },
    }).then(response => {
        const fileRepoResponse = FileRepoFile.fromFileRepo(response);

        if (response.error) {
            return { ...fileRepoResponse, name: fileName, originalFileId: fileId, sourceType: fileSourceType };
        } else {
            return { ...fileRepoResponse, originalFileId: fileId, sourceType: fileSourceType };
        }
    }).catch((response) => {
        return { name: fileName, originalFileId: fileId, sourceType: fileSourceType, error: fileErrors.unknown };
    });
}

/**
 * Gets a one time token for uploading a file to FileRepo
 * @param {File} file - The file to upload, from for example an <input type="file" />
 * @param {string} tokenProviderEndpoint - Endpoint to get the token from
 * @param {string} fallbackToken - Default token to return if get was not succeeded
 * @returns {Promise} A promise that resolves to { token }
 */
export function getOneTimeToken(file, tokenProviderEndpoint, fallbackToken) {
    return get(tokenProviderEndpoint.replace('~', '') + '?FileName=' + encodeURIComponent(file.name))
        .then(token => {
            return token;
        }).catch(() => {
            return fallbackToken;
        });
}
